'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import {
  FacebookShareButton,
  FacebookMessengerShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  EmailShareButton,
} from 'next-share';

import Breadcrumb from '@/components/Breadcrumb';
import ProductDetailImage from '@/components/ProductDetailImage';
import ProductDetailImageCarousel from '@/components/ProductDetailImageCarousel';
import ProductOptions from '@/components/ProductOptions';
import ProductSlider from '@/components/ProductSlider';
import ProductSection from '@/components/ProductSection';

import { FB_APP_ID, RVI_LS_PROPERTY_NAME } from '@/constants';
import { stripHTML } from '@/utils/helper';
import { useProduct, useRecentlyViewedProducts, useRecommendedProduct, useCrossCategoryProduct, useUpdateInfoGraphic } from '@/hooks/product';
import { IProductDetails } from '@/interfaces/product';
import { getDataLayer } from '@/utils/data-layer';
import { useDeliveryOption } from '@/hooks/useDeliveryOptions';

interface ProductDetailClientProps {
  seName: string;
  dehydratedState?: any;
}

const ProductDetailClient: React.FC<ProductDetailClientProps> = ({ seName }) => {
  const [shareLink, setShareLink] = useState('');
  const [arrayOfProductIds, setArrayOfProductIds] = useState<string[]>([]);
  const [showFullImage, setShowFullImage] = useState<string[] | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRefetchOnMount, setIsRefetchOnMount] = useState(true);

  const { selectedStore, selectedStoreIds } = useDeliveryOption();

  const hasValidSelectedStore = selectedStoreIds.length > 0 && selectedStoreIds[0] !== '';
  const getStoreIds = () => {
    if (selectedStoreIds.length > 0) return selectedStoreIds;
    if (selectedStore?.Id) return [selectedStore.Id];
    return undefined;
  };

  const { data: product } = useProduct(seName, Boolean(selectedStore?.Id || hasValidSelectedStore), getStoreIds());
  const { data: recentlyViewedProducts } = useRecentlyViewedProducts(arrayOfProductIds);
  const { data: recommendedProducts } = useRecommendedProduct(seName);
  const { data: crossCategoryProducts } = useCrossCategoryProduct(seName);
  const { mutate: mutateInfoGraphic } = useUpdateInfoGraphic(seName, false);

  // Restore recently viewed items + trigger infographic update
  useEffect(() => {
    let stringifiedRVIs = localStorage.getItem(RVI_LS_PROPERTY_NAME);
    if (stringifiedRVIs) {
      try {
        const tempArrayOfProductIds = JSON.parse(stringifiedRVIs);
        if (Array.isArray(tempArrayOfProductIds)) {
          setArrayOfProductIds(tempArrayOfProductIds);
        }
      } catch (e) {
        console.error('JSON parse error:', e);
      }
    }
    setShareLink(globalThis.location.href);
    mutateInfoGraphic(seName);
  }, [seName]);

  // Push product view data to dataLayer
  useEffect(() => {
    if (product && isRefetchOnMount) {
      setIsRefetchOnMount(false);
      const obj: Record<string, string> = {};

      if (product.CategoryName) {
        const categories = product.CategoryName.split(',');
        categories.forEach((cat, i) => (obj[`item_category${i ? i : ''}`] = cat));
      }

      if (!product.IsGiftVoucher && product.Gender) {
        obj.gender = product.Gender;
      }

      const itemArray = [
        {
          item_id: product.SeName,
          item_name: product.Name,
          item_sku: product.Sku,
          item_brand: product.ProductBrand?.Name,
          price: product.ProductPrice?.Price,
          quantity: product.StockQuantity,
          item_variant: product.Variations?.map((v) => v.VariantSku),
          color: product.AvailableColors?.map((c) => c.ColorName),
          size: product.Variations?.map((v) => v.ProductAttributeValue),
          ...obj,
        },
      ];

      const dataLayer = getDataLayer();
      if (dataLayer) {
        dataLayer.push({ ecommerce: null });
        dataLayer.push({ event: 'select_item', ecommerce: { items: itemArray } });
        dataLayer.push({ ecommerce: null });
        dataLayer.push({ event: 'view_item', ecommerce: { items: itemArray } });
      }
    }
  }, [product, isRefetchOnMount]);

  useEffect(() => setIsRefetchOnMount(true), [seName]);

  const completeLookSlides = product?.CompleteTheLooks?.map((slide) => ({
    id: slide.Id,
    FeaturedImageUrl: slide.FeaturedImageUrl,
    name: slide.Name,
    options: slide.Colors,
    productPrice: slide.ProductPrice,
    seName: slide.SeName,
    altText: slide.AltText,
  }));

  const relatedProductSlides = product?.YouMayAlsoLikes?.map((slide) => ({
    id: slide.Id,
    FeaturedImageUrl: slide.FeaturedImageUrl,
    name: slide.Name,
    options: slide.Colors,
    productPrice: slide.ProductPrice,
    seName: slide.SeName,
    altText: slide.AltText,
  }));

  const recommendedProductSlides = recommendedProducts?.map((slide) => ({
    id: slide.Id,
    FeaturedImageUrl: slide.FeaturedImageUrl,
    name: slide.Name,
    options: slide.Colors,
    productPrice: slide.ProductPrice,
    seName: slide.SeName,
    altText: slide.AltText,
  }));

  const otherRelatedProductSlides = product?.OthersAlsoLikes?.map((slide) => ({
    id: slide.Id,
    FeaturedImageUrl: slide.FeaturedImageUrl,
    name: slide.Name,
    options: slide.Colors,
    productPrice: slide.ProductPrice,
    seName: slide.SeName,
    altText: slide.AltText,
  }));

  const crossCategorySlides = crossCategoryProducts?.map((slide) => ({
    id: slide.Id,
    FeaturedImageUrl: slide.FeaturedImageUrl,
    name: slide.Name,
    options: slide.Colors,
    productPrice: slide.ProductPrice,
    seName: slide.SeName,
    altText: slide.AltText,
  }));

  const recentlyViewedProductSlides = recentlyViewedProducts?.map((slide) => ({
    id: slide.Id,
    FeaturedImageUrl: slide.FeaturedImageUrl,
    name: slide.Name,
    options: slide.Colors,
    productPrice: slide.ProductPrice,
    seName: slide.SeName,
    altText: slide.AltText,
  }));

  const accordionImages = ['https://storage.apex4u.com/Materials.png', 'https://storage.apex4u.com/Shipping.png'];

  const getFeaturedImage = (productImageList: IProductDetails['ProductImageList']) => productImageList.find((data) => data.IsFeaturedImage === true);

  return (
    <div className="container">
      <div className="flex justify-center">
        <div className="lg:-mx-13 w-full justify-center lg:flex lg:px-11">
          <div className="flex grow flex-col lg:min-w-[40rem]">
            {product?.Breadcrumbs && <Breadcrumb className="" bred={product?.Breadcrumbs} />}
            <ProductDetailImage productDetail={product} setShowFullImage={setShowFullImage} setCurrentIndex={setCurrentIndex} />
            {showFullImage && (
              <ProductDetailImageCarousel
                imageList={showFullImage}
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
                onClose={() => setShowFullImage(null)}
              />
            )}
          </div>

          {/* RIGHT SECTION */}
          <div className="mt-12 lg:w-full lg:min-w-[40rem] lg:max-w-[40rem] lg:pl-[60px]">
            <ProductOptions quickView={false} product={product || ({} as IProductDetails)} productInfoGraphic={product?.ProductViewed} />

            {(stripHTML(product?.ShortDescription) || stripHTML(product?.FullDescription)) && (
              <ProductSection className="description my-5" icons="/images/description.png" title="Description" id="description">
                <div id="description">
                  <div className="mx-6 text-justify md:mx-0 md:ml-6" dangerouslySetInnerHTML={{ __html: product?.ShortDescription || '' }} />
                  <div className="mx-6 text-justify md:mx-0 md:ml-6" dangerouslySetInnerHTML={{ __html: product?.FullDescription || '' }} />
                </div>
              </ProductSection>
            )}

            {product?.ProductSpecifications?.map(
              (spec, index) =>
                stripHTML(spec.ValueRaw) && (
                  <ProductSection
                    id={`${spec.SpecificationAttributeName}-${index}`}
                    className={`my-5 ${spec.SpecificationAttributeName.toLowerCase().replaceAll(' ', '-')}`}
                    icons={accordionImages[index] || '/images/description.png'}
                    key={spec.SpecificationAttributeName}
                    title={spec.SpecificationAttributeName}
                  >
                    <div
                      id={`${spec.SpecificationAttributeName}-${index}`}
                      className="mx-6 text-justify md:mx-0 md:ml-6"
                      dangerouslySetInnerHTML={{ __html: spec.ValueRaw }}
                    />
                  </ProductSection>
                ),
            )}

            {stripHTML(product?.Disclaimer) && (
              <ProductSection className="disclaimer my-5" icons="/images/disclaimer.png" title="Disclaimer" id="disclaimer">
                <div id="disclaimer">
                  <div className="mx-6 text-justify md:mx-0 md:ml-6" dangerouslySetInnerHTML={{ __html: product?.Disclaimer || '' }} />
                </div>
              </ProductSection>
            )}

            {/* SOCIAL SHARE BUTTONS */}
            <div className="mt-5 flex items-center justify-center">
              <div data-testid="share-facebook-button">
                <FacebookShareButton url={shareLink}>
                  <div className="mr-1.5 flex aspect-square w-[32px] items-center justify-center rounded-md bg-[#4267B2] transition duration-500 hover:-translate-y-2 hover:opacity-80">
                    <div className="relative aspect-square w-[16px]">
                      <Image unoptimized fill quality={100} src="/icons/facebook.svg" alt="facebook" />
                    </div>
                  </div>
                </FacebookShareButton>
              </div>

              <div data-testid="share-messenger-button">
                <FacebookMessengerShareButton url={shareLink} title={product?.Name} appId={FB_APP_ID}>
                  <div className="mx-1.5 flex aspect-square w-[32px] items-center justify-center rounded-md bg-[#448AFF] transition duration-500 hover:-translate-y-2 hover:opacity-80">
                    <div className="relative aspect-square w-[16px]">
                      <Image unoptimized fill quality={100} src="/icons/messenger.svg" alt="messenger" />
                    </div>
                  </div>
                </FacebookMessengerShareButton>
              </div>

              <div data-testid="share-linkedin-button">
                <LinkedinShareButton url={shareLink} title={product?.Name}>
                  <div className="mx-1.5 flex aspect-square w-[32px] items-center justify-center rounded-md bg-[#0077b5] transition duration-500 hover:-translate-y-2 hover:opacity-80">
                    <div className="relative aspect-square w-[16px]">
                      <Image unoptimized fill quality={100} src="/icons/linkedin.svg" alt="linkedin" />
                    </div>
                  </div>
                </LinkedinShareButton>
              </div>

              <div data-testid="share-twitter-button">
                <TwitterShareButton url={shareLink} title={product?.Name}>
                  <div className="mx-1.5 flex aspect-square w-[32px] items-center justify-center rounded-md bg-[#55acee] transition duration-500 hover:-translate-y-2 hover:opacity-80">
                    <div className="relative aspect-square w-[16px]">
                      <Image unoptimized fill quality={100} src="/icons/twitter.svg" alt="twitter" />
                    </div>
                  </div>
                </TwitterShareButton>
              </div>

              <div data-testid="share-whatsapp-button">
                <WhatsappShareButton url={shareLink} title={product?.Name}>
                  <div className="mx-1.5 flex aspect-square w-[32px] items-center justify-center rounded-md bg-[#25d366] transition duration-500 hover:-translate-y-2 hover:opacity-80">
                    <div className="relative aspect-square w-[16px]">
                      <Image unoptimized fill quality={100} src="/icons/whatsapp.svg" alt="whatsapp" />
                    </div>
                  </div>
                </WhatsappShareButton>
              </div>

              <div data-testid="share-email-button">
                <EmailShareButton url={shareLink}>
                  <div className="mx-1.5 flex aspect-square w-[32px] items-center justify-center rounded-md bg-[#645EA4] transition duration-500 hover:-translate-y-2 hover:opacity-80">
                    <div className="relative aspect-square w-[16px]">
                      <Image unoptimized fill quality={100} src="/icons/email1.svg" alt="email" />
                    </div>
                  </div>
                </EmailShareButton>
              </div>

              <button
                data-testid="print-button"
                className="mx-1.5 flex aspect-square h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-md bg-[#222] transition duration-500 hover:-translate-y-2 hover:opacity-80"
                id="print"
                onClick={() => window?.print()}
                aria-label="Print page"
                type="button"
              >
                <Image unoptimized width={16} height={16} src="/icons/print1.svg" alt="" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PRODUCT SLIDERS */}
      <div className="overflow-hidden">
        <section className="content-section">
          {!!completeLookSlides?.length && (
            <div className="mt-10">
              <p className="product-carousel__heading">
                <span className="product-carousel__title">Complete the look</span>
              </p>
              <ProductSlider
                data-testid="complete-the-look-slider"
                navigation
                className="mt-3"
                sliderType="complete-the-look"
                products={completeLookSlides}
              />
            </div>
          )}

          {!!crossCategorySlides?.length && (
            <div className="mt-8">
              <p className="product-carousel__heading">
                <span className="product-carousel__title">People who bought this also buy these</span>
              </p>
              <ProductSlider
                data-testid="cross-category-suggestions-slider"
                navigation
                className="mt-3"
                sliderType="cross-category-suggestions"
                products={crossCategorySlides}
              />
            </div>
          )}

          {!!relatedProductSlides?.length && (
            <div className="mt-8">
              <p className="product-carousel__heading">
                <span className="product-carousel__title">You may also like</span>
              </p>
              <ProductSlider
                data-testid="you-may-also-like-slider"
                navigation
                className="mt-3"
                sliderType="you-may-also-like"
                products={relatedProductSlides}
              />
            </div>
          )}

          {!!recommendedProductSlides?.length && (
            <div className="mt-8">
              <p className="product-carousel__heading">
                <span className="product-carousel__title">Recommended products</span>
              </p>
              <ProductSlider
                data-testid="recommended-product-slider"
                navigation
                className="mt-3"
                sliderType="recommended-product"
                products={recommendedProductSlides}
              />
            </div>
          )}

          {!!recentlyViewedProductSlides?.length && (
            <div className="mt-8">
              <p className="product-carousel__heading">
                <span className="product-carousel__title">Recently viewed items</span>
              </p>
              <ProductSlider
                data-testid="recently-viewed-items-slider"
                navigation
                className="mt-3"
                sliderType="recently-viewed-items"
                products={recentlyViewedProductSlides}
              />
            </div>
          )}

          {!!otherRelatedProductSlides?.length && (
            <div className="mt-8">
              <p className="product-carousel__heading">
                <span className="product-carousel__title">Trending Now</span>
              </p>
              <ProductSlider
                data-testid="trending-now-slider"
                navigation
                className="mt-3"
                sliderType="trending-now"
                products={otherRelatedProductSlides}
              />
            </div>
          )}
        </section>
      </div>

      <style jsx>{`
        .product-carousel__heading {
          position: relative;
          margin: 0 0 20px;
          line-height: 23px;
          text-align: center;
        }
        .product-carousel__heading:before {
          position: absolute;
          margin-left: -3rem;
          margin-right: -3rem;
          top: 14px;
          right: 0;
          left: 0;
          height: 1px;
          content: '';
          background: #000;
        }
        .product-carousel__heading:after {
          position: absolute;
          top: 14px;
          right: 0;
          left: 0;
          height: 1px;
          content: '';
          padding-top: 24px;
          border-left: 1px solid black;
          border-right: 1px solid black;
          margin-left: -3rem;
          margin-right: -3rem;
        }
        .product-carousel__title {
          letter-spacing: 0.2em;
          text-transform: uppercase;
          position: relative;
          display: inline-block;
          padding: 0 20px;
          font-size: 16px;
          line-height: 28px;
          background: #fff;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default ProductDetailClient;
